import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as he from 'he';
import { ApiValidation } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import {
  CATEGORY_COLOR_MAP,
  DisplayColor,
  StoreAttributeCategoryViewModel,
} from '../../../../models/store-attribute.model';
import { StoreAttributeService } from '../../../../services/store-attribute.service';

export interface ManageCategoriesDialogData {
  categories: StoreAttributeCategoryViewModel[];
}

interface ManagedCategory {
  id: string;
  name: string;
  color: DisplayColor;
  attributeTypeCount: number;
}

@Component({
  selector: 'app-manage-categories-dialog',
  templateUrl: './manage-categories-dialog.component.html',
  styleUrls: ['./manage-categories-dialog.component.scss'],
})
export class ManageCategoriesDialogComponent implements OnInit {
  categories: ManagedCategory[] = [];
  private initialOrderIds: string[] = [];
  isSaving = false;

  private readonly errorSnackBarConfig = {
    verticalPosition: 'top' as const,
    panelClass: ['snackbar-error'],
  };

  constructor(
    private dialogRef: MatDialogRef<ManageCategoriesDialogComponent>,
    private storeAttributeService: StoreAttributeService,
    private matSnackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: ManageCategoriesDialogData,
  ) {}

  ngOnInit(): void {
    const sorted = [...this.data.categories].sort((a, b) => a.displayOrder - b.displayOrder);
    this.categories = sorted.map((category) => ({
      id: category.id,
      name: category.name,
      color: category.color,
      attributeTypeCount: category.attributeTypes.length,
    }));
    this.initialOrderIds = this.categories.map((category) => category.id);
  }

  get hasOrderChanged(): boolean {
    const currentIds = this.categories.map((category) => category.id);
    return currentIds.some((id, index) => id !== this.initialOrderIds[index]);
  }

  getCategoryColor(color: DisplayColor): string {
    return CATEGORY_COLOR_MAP[color];
  }

  getAttributeTypeCountLabel(count: number): string {
    return he.escape(`${count} attribute type${count === 1 ? '' : 's'}`);
  }

  drop(event: CdkDragDrop<ManagedCategory[]>): void {
    moveItemInArray(this.categories, event.previousIndex, event.currentIndex);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (!this.hasOrderChanged || this.isSaving) {
      return;
    }

    this.isSaving = true;
    const orderedIds = this.categories.map((category) => category.id);

    this.storeAttributeService.reorderCategories(orderedIds).subscribe({
      next: () => {
        this.isSaving = false;
        this.storeAttributeService.reload();
        this.matSnackBar.open('Category order updated', 'OK', DEFAULT_SNACKBAR_CONFIG);
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving = false;
        this.handleApiError(error);
        this.storeAttributeService.reload();
      },
    });
  }

  private handleApiError(error: HttpErrorResponse): void {
    if (error.status === 500) {
      this.matSnackBar.open('Error reordering categories', 'Error', this.errorSnackBarConfig);
      return;
    }

    if (error.status === 400) {
      this.handleBadRequest(error);
      return;
    }

    if (error.status === 429) {
      this.matSnackBar.open(
        `Too many requests. Please wait a moment and retry. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    if (error.status === 504 || error.status === 408) {
      this.matSnackBar.open(
        `Server is taking too long to respond. Please try again. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    if (error.status === 0) {
      this.matSnackBar.open(
        `Request timed out or network issue. Check your connection. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    const message =
      error?.error?.message ||
      error?.message ||
      `Unexpected error. (statusCode: ${error.status})`;
    this.matSnackBar.open(message, 'Error', this.errorSnackBarConfig);
  }

  private handleBadRequest(error: HttpErrorResponse): void {
    const apiValidations = error.error;

    if (Array.isArray(apiValidations) && typeof apiValidations[0] === 'string') {
      this.matSnackBar.open(apiValidations[0], 'Error', this.errorSnackBarConfig);
      return;
    }

    if (Array.isArray(apiValidations)) {
      const errorMessages: string[] = [];
      apiValidations.forEach((validation: ApiValidation) => {
        if (validation?.ErrorMessage) {
          errorMessages.push(validation.ErrorMessage);
        }
      });

      if (errorMessages.length > 0) {
        this.matSnackBar.open(errorMessages[0], 'Error', this.errorSnackBarConfig);
      }
      return;
    }

    if (apiValidations?.error?.message) {
      this.matSnackBar.open(apiValidations.error.message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (apiValidations?.message) {
      this.matSnackBar.open(apiValidations.message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (typeof apiValidations === 'string') {
      this.matSnackBar.open(apiValidations, 'Error', this.errorSnackBarConfig);
      return;
    }

    this.matSnackBar.open('Error reordering categories', 'Error', this.errorSnackBarConfig);
  }
}
